import React from 'react';
import { Download } from 'lucide-react';
import { SearchInput } from '../findings/filters/SearchInput';
import { FilterSelect } from '../findings/filters/FilterSelect';
import { FilterButton } from '../findings/filters/FilterButton';
import { assetTypeOptions, assetStatusOptions } from '../../data/assets-filters';
import { assetSeverityOptions, assetTimeframeOptions } from '../../data/asset-severity-filters';

interface AssetsFiltersProps {
  assetType?: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedType: string;
  onTypeChange: (type: string) => void;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  // Risk score filters (maps to backend risk_score_min, risk_score_max, risk_score)
  selectedSeverity?: string;
  onSeverityChange?: (severity: string) => void;
  selectedRiskScoreMin?: string;
  onRiskScoreMinChange?: (score: string) => void;
  selectedRiskScoreMax?: string;
  onRiskScoreMaxChange?: (score: string) => void;
  // Findings count filters (maps to backend findings_min, findings_max)
  selectedFindingsMin?: number | undefined;
  onFindingsMinChange?: (count: number | undefined) => void;
  selectedFindingsMax?: number | undefined;
  onFindingsMaxChange?: (count: number | undefined) => void;
  selectedOrdering?: string;
  onOrderingChange?: (ordering: string) => void;
  // Time-based filters
  selectedTimeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
  // Tag and region filters
  selectedTags: string[];
  onTagChange: (tag: string, checked: boolean) => void;
  selectedRegions: string[];
  onRegionChange: (region: string, checked: boolean) => void;
  // Actions
  onReset: () => void;
  onExport: () => void;
}

export const AssetsFilters: React.FC<AssetsFiltersProps> = ({
  assetType,
  searchQuery,
  onSearchChange,
  selectedType,
  onTypeChange,
  selectedStatus,
  onStatusChange,
  selectedSeverity = 'all',
  onSeverityChange = () => {},
  selectedRiskScoreMin,
  onRiskScoreMinChange = () => {},
  selectedRiskScoreMax,
  onRiskScoreMaxChange = () => {},
  selectedFindingsMin,
  onFindingsMinChange = () => {},
  selectedFindingsMax,
  onFindingsMaxChange = () => { },
  selectedOrdering = 'riskScore',
  onOrderingChange = () => {},
  selectedTimeframe = 'all',
  onTimeframeChange = () => {},
  selectedTags,
  onTagChange,
  selectedRegions,
  onRegionChange,
  onReset,
  onExport,
}) => {
  // Risk score options for filtering
  const riskScoreOptions = [
    { value: 'no_filter', label: 'No Filter' },
    { value: 'a+', label: 'A+', color: 'text-emerald-500 bg-emerald-500/10' },
    { value: 'a', label: 'A', color: 'text-green-500 bg-green-500/10' },
    { value: 'b', label: 'B', color: 'text-yellow-500 bg-yellow-500/10' },
    { value: 'c', label: 'C', color: 'text-orange-500 bg-orange-500/10' },
    { value: 'f', label: 'F', color: 'text-red-500 bg-red-500/10' },
  ];

  // Findings count options for filtering
  const findingsCountOptions = [
    { value: 'no_filter', label: 'No Filter' },
    { value: '0', label: '0' },
    { value: '5', label: '5' },
    { value: '10', label: '10' },
    { value: '20', label: '20' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
  ];
  const orderingOptions = [
    { value: 'no_filter', label: 'No Filter' },
    { value: 'riskScore', label: 'Risk Score' },
    { value: 'findings', label: 'Findings' },
  ];

  const filterSections = [
    {
      title: 'Tags',
      options: [
        { value: 'production', label: 'Production', color: 'text-red-500' },
        { value: 'staging', label: 'Staging', color: 'text-yellow-500' },
        { value: 'development', label: 'Development', color: 'text-blue-500' },
      ],
      selectedValues: selectedTags,
      onValueChange: onTagChange,
    },
    {
      title: 'Regions',
      options: [
        { value: 'na', label: 'North America', color: 'text-blue-500' },
        { value: 'eu', label: 'Europe', color: 'text-purple-500' },
        { value: 'asia', label: 'Asia Pacific', color: 'text-emerald-500' },
        { value: 'sa', label: 'South America', color: 'text-yellow-500' },
      ],
      selectedValues: selectedRegions,
      onValueChange: onRegionChange,
    },
    // {
    //   title: 'Risk Score',
    //   options: riskScoreOptions.slice(1), // Remove the "No Filter" option for the filter button
    //   selectedValues: [],
    //   onValueChange: () => {}, // This will be handled by the dedicated filter selects
    // },
    // {
    //   title: 'Ordering',
    //   options: orderingOptions,
    //   selectedValues: [],
    //   onValueChange: () => {}, // This will be handled by the dedicated filter selects
    // },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Search */}
        <div className="flex-1">
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Search assets by name, type, or tag..."
          />
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap items-center gap-4">
          {/* <div className="w-48">
            <FilterSelect
              value={selectedType}
              onValueChange={onTypeChange}
              options={assetTypeOptions}
              placeholder="All Types"
              label="Type"
            />
          </div>

          <div className="w-48">
            <FilterSelect
              value={selectedStatus}
              onValueChange={onStatusChange}
              options={assetStatusOptions}
              placeholder="All Statuses"
              label="Status"
            />
          </div>

          <div className="w-48">
            <FilterSelect
              value={selectedSeverity}
              onValueChange={onSeverityChange}
              options={assetSeverityOptions}
              placeholder="All Severities"
              label="Severity"
            />
          </div> */}

          {/* Risk Score Min Filter - maps to risk_score_min in backend */}
          {assetType !== 'ports' && (
            <div className="w-48">
              <FilterSelect
                value={selectedRiskScoreMin || 'no_filter'}
                onValueChange={(value) => onRiskScoreMinChange(value === 'no_filter' ? '' : value)}
                options={riskScoreOptions}
                placeholder="Risk Score"
                label="Risk Score"
              />
            </div>
          )}

          {/* Risk Score Max Filter - maps to risk_score_max in backend */}
          {/* <div className="w-48">
            <FilterSelect
              value={selectedRiskScoreMax || 'no_filter'}
              onValueChange={(value) => onRiskScoreMaxChange(value === 'no_filter' ? '' : value)}
              options={riskScoreOptions}
              placeholder="Max Risk Score"
              label="Max Risk Score"
            />
          </div> */}

          {/* Findings Min Filter - maps to findings_min in backend */}
          {assetType !== 'ports' && (
            <div className="w-48">
              <FilterSelect
                value={selectedFindingsMin?.toString() || 'no_filter'}
                onValueChange={(value) => {
                  if (value === 'no_filter') {
                    onFindingsMinChange(undefined);
                } else {
                  onFindingsMinChange(parseInt(value) || 0);
                }
              }}
              options={findingsCountOptions}
              placeholder="Min Findings"
              label="Min Findings"
            />
          </div>
          )}

          {/* Findings Max Filter - maps to findings_max in backend */}
          {assetType !== 'ports' && (
            <div className="w-48">
              <FilterSelect
                value={selectedFindingsMax?.toString() || 'no_filter'}
                onValueChange={(value) => {
                  if (value === 'no_filter') {
                    onFindingsMaxChange(undefined);
                  } else {
                    onFindingsMaxChange(parseInt(value) || 0);
                  }
                }}
                options={findingsCountOptions}
                placeholder="Max Findings"
                label="Max Findings"
              />
            </div>
          )}
          <div className="w-48">
            <FilterSelect
              value={selectedOrdering || 'no_filter'}
              onValueChange={(value) => {
                if (value === 'no_filter') {
                  onOrderingChange('');
                } else {
                  onOrderingChange(value);
                }
              }}
              options={orderingOptions}
              placeholder="Ordering"
              label="Ordering"
            />
          </div>

          {/* <div className="w-48">
            <FilterSelect
              value={selectedTimeframe}
              onValueChange={onTimeframeChange}
              options={assetTimeframeOptions}
              placeholder="All Time"
              label="Timeframe"
            />
          </div> */}

          <FilterButton sections={filterSections} onReset={onReset} />

          {/* <button
            onClick={onExport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500/10 text-blue-500 rounded-lg hover:bg-blue-500/20 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span className="text-sm">Export</span>
          </button> */}
        </div>
      </div>

      {/* Active Filters */}
      {(selectedTags.length > 0 || selectedRegions.length > 0 || 
        selectedRiskScoreMin || selectedRiskScoreMax || 
        selectedFindingsMin || selectedFindingsMax) && (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <span>Active filters:</span>
          {/* Risk Score Min filter pill */}
          {selectedRiskScoreMin && (
            <span className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded-lg">
              Min Risk: {selectedRiskScoreMin.toUpperCase()}
            </span>
          )}
          {/* Risk Score Max filter pill */}
          {selectedRiskScoreMax && (
            <span className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded-lg">
              Max Risk: {selectedRiskScoreMax.toUpperCase()}
            </span>
          )}
          {/* Findings Min filter pill */}
          {selectedFindingsMin !== undefined && selectedFindingsMin > 0 && (
            <span className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded-lg">
              Min Findings: {selectedFindingsMin}
            </span>
          )}
          {/* Findings Max filter pill */}
          {selectedFindingsMax !== undefined && selectedFindingsMax > 0 && (
            <span className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded-lg">
              Max Findings: {selectedFindingsMax}
            </span>
          )}
          {/* Other active filter pills would go here */}
        </div>
      )}
    </div>
  );
};